import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Menu, Modal, Tooltip, message } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useSetAtom } from 'jotai';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableRubric,
  type DraggableStateSnapshot,
  DragStart,
  type DragUpdate,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AiRealtimeKeywordItem,
  FolderNode,
  TeambinderAiApiService,
} from '@src/api';
import { ApiError } from '@src/api/core/ApiError';
import { themeMobileNavAtom } from '@src/store/theme';
import utils from '@src/utils/utils';
import EllipsisDropdown from '@components/shared/EllipsisDropdown';
import RealtimeKeywordModal from '@src/components/shared/RealtimeKeywordModal';
import KeywordDefinitionModal from '@src/components/shared/KeywordDefinitionModal';
import DynamicDropdownMenu from '@src/components/shared/DynamicDropdownMenu';

const { confirm } = Modal;

const QUERY_KEY_TREE = ['realtime-keyword-tree'];
const QUERY_KEY_FLAT = ['realtime-keywords'];
const QUERY_KEY_FOLDERS = ['realtime-keyword-folders'];
const UNASSIGNED_DROPPABLE_ID = 'keywords:unassigned';
const FOLDER_DROPPABLE_PREFIX = 'keywords:folder:';
const FOLDER_ORDER_DROPPABLE_ID = 'folder-order';
const MAX_FOLDER_COUNT = 10;
const FOLDER_ROW_LEFT = 14;
const KEYWORD_TEXT_LEFT = 36;
const FOLDER_CHILD_TEXT_LEFT = 40;

type FolderModalState =
  | { mode: 'create'; folder?: undefined }
  | { mode: 'rename'; folder: FolderNode };

type OrderedFolderNode = FolderNode & { folderId: string };

type KeywordTreeData = {
  folders: FolderNode[];
  unassigned: AiRealtimeKeywordItem[];
};

const reorderItems = <T,>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number,
) => {
  if (
    sourceIndex < 0 ||
    sourceIndex >= items.length ||
    destinationIndex < 0 ||
    destinationIndex > items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(destinationIndex, 0, movedItem);
  return nextItems;
};

const reorderFoldersInTree = (
  tree: KeywordTreeData,
  sourceIndex: number,
  destinationIndex: number,
): KeywordTreeData => ({
  ...tree,
  folders: reorderItems(tree.folders, sourceIndex, destinationIndex),
});

const moveKeywordInTree = ({
  tree,
  keywordId,
  source,
  destination,
}: {
  tree: KeywordTreeData;
  keywordId: string;
  source: DropResult['source'];
  destination: NonNullable<DropResult['destination']>;
}): KeywordTreeData => {
  const sourceFolderId = getFolderIdFromDroppable(source.droppableId);
  const destinationFolderId = getFolderIdFromDroppable(destination.droppableId);
  const nextFolders = tree.folders.map((folder) => ({
    ...folder,
    items: [...(folder.items ?? [])],
  }));
  const nextUnassigned = [...tree.unassigned];

  const getKeywordList = (folderId: string | null) => {
    if (folderId === null) {
      return nextUnassigned;
    }
    return nextFolders.find((folder) => folder.folderId === folderId)?.items;
  };

  const sourceItems = getKeywordList(sourceFolderId);
  const destinationItems = getKeywordList(destinationFolderId);

  if (!sourceItems || !destinationItems) {
    return tree;
  }

  const sourceIndex =
    sourceItems[source.index]?.keywordId === keywordId
      ? source.index
      : sourceItems.findIndex((item) => item.keywordId === keywordId);

  if (sourceIndex < 0) {
    return tree;
  }

  const [movedItem] = sourceItems.splice(sourceIndex, 1);
  destinationItems.splice(destination.index, 0, {
    ...movedItem,
    folderId: destinationFolderId ?? undefined,
  });

  return {
    ...tree,
    folders: nextFolders,
    unassigned: nextUnassigned,
  };
};

const getApiErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    return error.body?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '처리 중 오류가 발생했습니다.';
};

const getKeywordAreaId = (folderId?: string | null) =>
  folderId ? `${FOLDER_DROPPABLE_PREFIX}${folderId}` : UNASSIGNED_DROPPABLE_ID;

const getFolderIdFromDroppable = (droppableId: string) => {
  if (droppableId === UNASSIGNED_DROPPABLE_ID) {
    return null;
  }
  if (droppableId.startsWith(FOLDER_DROPPABLE_PREFIX)) {
    return droppableId.replace(FOLDER_DROPPABLE_PREFIX, '');
  }
  return null;
};

const getCurrentFeaturePath = (pathname: string) => {
  if (pathname.startsWith('/app/ai-insight')) {
    return '/app/ai-insight';
  }
  if (pathname.startsWith('/app/ai-report')) {
    return '/app/ai-report';
  }
  if (pathname.startsWith('/app/issue-table')) {
    return '/app/issue-table';
  }
  return '/app/realtime';
};

const getCurrentFeatureEmptyPath = (pathname: string) => {
  if (pathname.startsWith('/app/issue-table')) {
    return '/app/issue-table';
  }
  return `${getCurrentFeaturePath(pathname)}/new`;
};

const getItemsByDroppableId = (
  folders: FolderNode[],
  unassigned: AiRealtimeKeywordItem[],
  droppableId: string,
) => {
  if (droppableId === UNASSIGNED_DROPPABLE_ID) {
    return unassigned;
  }

  const folderId = getFolderIdFromDroppable(droppableId);
  return folders.find((folder) => folder.folderId === folderId)?.items ?? [];
};

const getPrevKeywordId = ({
  source,
  destination,
  folders,
  unassigned,
}: {
  source: DropResult['source'];
  destination: NonNullable<DropResult['destination']>;
  folders: FolderNode[];
  unassigned: AiRealtimeKeywordItem[];
}) => {
  const destinationItems = getItemsByDroppableId(
    folders,
    unassigned,
    destination.droppableId,
  );

  if (source.droppableId === destination.droppableId) {
    if (source.index < destination.index) {
      return destinationItems[destination.index]?.keywordId ?? null;
    }
    return destination.index === 0
      ? null
      : destinationItems[destination.index - 1]?.keywordId ?? null;
  }

  return destination.index === 0
    ? null
    : destinationItems[destination.index - 1]?.keywordId ?? null;
};

const KeywordFolderTree = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const setMobileNav = useSetAtom(themeMobileNavAtom);
  const isMobile = utils.isMobile();
  const suppressFolderClickRef = useRef(false);
  const dndOperationIdRef = useRef(0);
  const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoExpandFolderIdRef = useRef<string | null>(null);

  const selectedKeywordId = searchParams.get('id');
  const [showAddKeywordModal, setShowAddKeywordModal] = useState(false);
  const [folderModal, setFolderModal] = useState<FolderModalState | null>(null);
  const [folderName, setFolderName] = useState('');
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<{
    keyword: string;
    keywordId: string;
  } | null>(null);
  const { data: keywordTree, refetch: refetchKeywordTree } = useQuery({
    queryKey: QUERY_KEY_TREE,
    retry: 2,
    queryFn: async () => {
      const result = await TeambinderAiApiService.getAiRealtimeKeywordTree();
      return {
        folders: result.data?.folders ?? [],
        unassigned: result.data?.unassigned ?? [],
      };
    },
  });

  const folders = keywordTree?.folders ?? [];
  const unassigned = keywordTree?.unassigned ?? [];
  const orderedFolders = useMemo(
    () =>
      folders.filter((folder): folder is OrderedFolderNode =>
        Boolean(folder.folderId),
      ),
    [folders],
  );
  const allKeywords = useMemo(
    () => [
      ...unassigned,
      ...orderedFolders.flatMap((folder) => folder.items ?? []),
    ],
    [orderedFolders, unassigned],
  );
  const folderOptions = useMemo(
    () =>
      orderedFolders.map((folder) => ({
        folderId: folder.folderId,
        name: folder.name || '이름 없는 폴더',
      })),
    [orderedFolders],
  );
  const folderLimitReached = orderedFolders.length >= MAX_FOLDER_COUNT;

  const isFolderNameDuplicated = useMemo(() => {
    const trimmed = folderName.trim();
    if (!trimmed || !folderModal) {
      return false;
    }
    const currentFolderId =
      folderModal.mode === 'rename' ? folderModal.folder.folderId : '';
    return orderedFolders.some(
      (folder) =>
        folder.name === trimmed && folder.folderId !== currentFolderId,
    );
  }, [folderName, folderModal, orderedFolders]);

  const invalidateKeywordQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_TREE }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_FLAT }),
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_FOLDERS }),
    ]);
  };

  const createFolderMutation = useMutation(TeambinderAiApiService.createFolder);
  const renameFolderMutation = useMutation(TeambinderAiApiService.renameFolder);
  const deleteFolderMutation = useMutation(TeambinderAiApiService.deleteFolder);
  const setCollapsedMutation = useMutation(TeambinderAiApiService.setCollapsed);
  const changeFolderOrderMutation = useMutation(
    TeambinderAiApiService.changeFolderOrder,
  );
  const changeKeywordOrderMutation = useMutation(
    TeambinderAiApiService.changeOrder,
  );
  const moveKeywordToFolderMutation = useMutation(
    TeambinderAiApiService.moveKeywordToFolder,
  );

  const closeMobileNav = () => {
    if (isMobile) {
      setMobileNav(false);
    }
  };

  const navigateToKeyword = (keywordId: string) => {
    navigate(`${getCurrentFeaturePath(location.pathname)}?id=${keywordId}`);
    closeMobileNav();
  };

  const navigateToEmptyFeature = () => {
    navigate(getCurrentFeatureEmptyPath(location.pathname));
    closeMobileNav();
  };

  const openFolderModal = (state: FolderModalState) => {
    setFolderModal(state);
    setFolderName(state.mode === 'rename' ? state.folder.name ?? '' : '');
  };

  const closeFolderModal = () => {
    setFolderModal(null);
    setFolderName('');
  };

  const validateFolderName = () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      message.error('폴더 이름을 입력해 주세요.');
      return null;
    }
    if (trimmedName.length > 20) {
      message.error('폴더 이름은 최대 20자까지 입력할 수 있습니다.');
      return null;
    }
    if (folderModal?.mode === 'create' && folderLimitReached) {
      message.error('폴더는 최대 10개까지 생성할 수 있습니다.');
      return null;
    }
    const duplicated = orderedFolders.some(
      (folder) =>
        folder.name === trimmedName &&
        folder.folderId !==
          (folderModal?.mode === 'rename' ? folderModal.folder.folderId : ''),
    );
    if (duplicated) {
      message.error('이미 사용 중인 이름입니다.');
      return null;
    }
    return trimmedName;
  };

  const submitFolderModal = async () => {
    const name = validateFolderName();
    if (!name || !folderModal) {
      return;
    }

    try {
      if (folderModal.mode === 'create') {
        await createFolderMutation.mutateAsync({
          requestBody: { name },
        });
      } else if (folderModal.folder.folderId) {
        await renameFolderMutation.mutateAsync({
          folderId: folderModal.folder.folderId,
          requestBody: { name },
        });
      }
      closeFolderModal();
      await invalidateKeywordQueries();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const removeKeywordList = (index: number) => {
    const item = allKeywords[index];
    if (!item?.keywordId) {
      return;
    }
    const keywordId = item.keywordId;

    confirm({
      title: '정말 삭제하시겠습니까?',
      async onOk() {
        try {
          await TeambinderAiApiService.deleteAiRealtimeKeyword({
            keywordId,
          });
          if (selectedKeywordId === keywordId) {
            navigate(getCurrentFeatureEmptyPath(location.pathname));
          }
          await invalidateKeywordQueries();
        } catch (error) {
          message.error(getApiErrorMessage(error));
        }
      },
      onCancel() {
        return null;
      },
      okText: '삭제',
      cancelText: '취소',
    });
  };

  const removeFolder = (folder: FolderNode) => {
    if (!folder.folderId) {
      return;
    }
    const folderId = folder.folderId;

    const hasKeywords = (folder.items ?? []).length > 0;
    confirm({
      title: `'${folder.name}' 폴더를 삭제하시겠습니까?`,
      content: hasKeywords
        ? '폴더만 삭제되며, 키워드는 보존됩니다.'
        : undefined,
      async onOk() {
        try {
          await deleteFolderMutation.mutateAsync({
            folderId,
          });
          await invalidateKeywordQueries();
        } catch (error) {
          message.error(getApiErrorMessage(error));
        }
      },
      okText: '삭제',
      cancelText: '취소',
    });
  };

  const toggleFolderCollapsed = async (folder: FolderNode) => {
    if (!folder.folderId) {
      return;
    }

    try {
      await setCollapsedMutation.mutateAsync({
        folderId: folder.folderId,
        requestBody: { isCollapsed: !folder.isCollapsed },
      });
      await invalidateKeywordQueries();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const cancelAutoExpand = () => {
    if (autoExpandTimerRef.current) {
      clearTimeout(autoExpandTimerRef.current);
      autoExpandTimerRef.current = null;
    }
    autoExpandFolderIdRef.current = null;
  };

  const optimisticallyExpandFolder = (folderId: string) => {
    const tree = queryClient.getQueryData<KeywordTreeData>(QUERY_KEY_TREE);
    if (!tree) return;
    queryClient.setQueryData<KeywordTreeData>(QUERY_KEY_TREE, {
      ...tree,
      folders: tree.folders.map((f) =>
        f.folderId === folderId ? { ...f, isCollapsed: false } : f,
      ),
    });
    setCollapsedMutation.mutate({
      folderId,
      requestBody: { isCollapsed: false },
    });
  };

  const onDragUpdate = (update: DragUpdate) => {
    const destination = update.destination;
    if (!destination || update.type !== 'KEYWORD') {
      cancelAutoExpand();
      return;
    }
    const folderId = getFolderIdFromDroppable(destination.droppableId);
    if (!folderId) {
      cancelAutoExpand();
      return;
    }
    const target = orderedFolders.find((f) => f.folderId === folderId);
    if (!target || !target.isCollapsed) {
      cancelAutoExpand();
      return;
    }
    if (autoExpandFolderIdRef.current === folderId) return;
    cancelAutoExpand();
    autoExpandFolderIdRef.current = folderId;
    autoExpandTimerRef.current = setTimeout(() => {
      optimisticallyExpandFolder(folderId);
      autoExpandTimerRef.current = null;
      autoExpandFolderIdRef.current = null;
    }, 600);
  };

  useEffect(() => cancelAutoExpand, []);

  const moveKeywordByMenu = async (
    keywordId: string,
    folderId: string | null,
  ) => {
    try {
      await moveKeywordToFolderMutation.mutateAsync({
        keywordId,
        requestBody: {
          folderId: folderId ?? undefined,
          prevId: undefined,
        },
      });
      await invalidateKeywordQueries();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const handleKeywordDefinition = (keyword: string, keywordId: string) => {
    setSelectedKeyword({ keyword, keywordId });
    setShowDefinitionModal(true);
  };

  const handleCompetitorKeywordDefinition = (
    keyword: string,
    competitorKeywordId: string,
  ) => {
    setSelectedKeyword({ keyword, keywordId: competitorKeywordId });
    setShowDefinitionModal(true);
  };

  const onDragStart = (start: DragStart) => {
    cancelAutoExpand();
    if (start.type === 'FOLDER') {
      suppressFolderClickRef.current = true;
    }
  };

  const onDragEnd = async (result: DropResult) => {
    cancelAutoExpand();
    const { source, destination, draggableId, type } = result;
    const releaseFolderClickSuppression = () => {
      if (type === 'FOLDER') {
        window.setTimeout(() => {
          suppressFolderClickRef.current = false;
        }, 0);
      }
    };

    if (!destination) {
      releaseFolderClickSuppression();
      return;
    }
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      releaseFolderClickSuppression();
      return;
    }

    const operationId = dndOperationIdRef.current + 1;
    dndOperationIdRef.current = operationId;
    let previousKeywordTree: KeywordTreeData | undefined;

    try {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY_TREE });
      previousKeywordTree =
        queryClient.getQueryData<KeywordTreeData>(QUERY_KEY_TREE);
      const optimisticBaseTree = previousKeywordTree ??
        keywordTree ?? {
          folders,
          unassigned,
        };
      const optimisticKeywordTree =
        type === 'FOLDER'
          ? reorderFoldersInTree(
              optimisticBaseTree,
              source.index,
              destination.index,
            )
          : moveKeywordInTree({
              tree: optimisticBaseTree,
              keywordId: draggableId,
              source,
              destination,
            });

      queryClient.setQueryData<KeywordTreeData>(
        QUERY_KEY_TREE,
        optimisticKeywordTree,
      );

      if (type === 'FOLDER') {
        const targetFolderId = draggableId;
        const prevFolderId =
          destination.index === 0
            ? undefined
            : orderedFolders[
                source.index < destination.index
                  ? destination.index
                  : destination.index - 1
              ]?.folderId;

        await changeFolderOrderMutation.mutateAsync({
          target: targetFolderId,
          prev: prevFolderId,
        });
      } else {
        const sourceFolderId = getFolderIdFromDroppable(source.droppableId);
        const destinationFolderId = getFolderIdFromDroppable(
          destination.droppableId,
        );
        const prevId = getPrevKeywordId({
          source,
          destination,
          folders: orderedFolders,
          unassigned,
        });

        if (sourceFolderId === destinationFolderId) {
          await changeKeywordOrderMutation.mutateAsync({
            target: draggableId,
            prev: prevId ?? undefined,
          });
        } else {
          await moveKeywordToFolderMutation.mutateAsync({
            keywordId: draggableId,
            requestBody: {
              folderId: destinationFolderId ?? undefined,
              prevId: prevId ?? undefined,
            },
          });
        }
      }
      await invalidateKeywordQueries();
    } catch (error) {
      message.error(getApiErrorMessage(error));
      if (dndOperationIdRef.current === operationId) {
        if (previousKeywordTree) {
          queryClient.setQueryData<KeywordTreeData>(
            QUERY_KEY_TREE,
            previousKeywordTree,
          );
        } else {
          await refetchKeywordTree();
        }
      } else {
        await invalidateKeywordQueries();
      }
    } finally {
      releaseFolderClickSuppression();
    }
  };

  const renderKeywordItem = (
    item: AiRealtimeKeywordItem,
    index: number,
    globalIndex: number,
    options?: { isFolderContent?: boolean; isLast?: boolean },
  ) => {
    const isFolderContent = options?.isFolderContent === true;
    const isLast = options?.isLast === true;
    const isActive =
      item.keywordId === selectedKeywordId &&
      [
        '/app/realtime',
        '/app/ai-insight',
        '/app/ai-report',
        '/app/issue-table',
      ].some((path) => location.pathname.startsWith(path));

    if (!item.keywordId) {
      return null;
    }
    const keywordId = item.keywordId;
    const keyword = item.keyword ?? '';

    return (
      <Draggable
        draggableId={keywordId}
        index={index}
        isDragDisabled={item.isSample}
        key={keywordId}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <div
              role="presentation"
              onClick={() => navigateToKeyword(keywordId)}
              onMouseEnter={(event) => {
                if (!isActive && !snapshot.isDragging) {
                  event.currentTarget.style.backgroundColor = '#F0F2F5';
                }
              }}
              onMouseLeave={(event) => {
                if (!isActive && !snapshot.isDragging) {
                  event.currentTarget.style.backgroundColor = 'inherit';
                }
              }}
              style={{
                position: 'relative',
                minHeight: '40px',
                width: '100%',
                fontSize: '14px',
                padding: `4px 8px 4px ${
                  isFolderContent ? FOLDER_CHILD_TEXT_LEFT : KEYWORD_TEXT_LEFT
                }px`,
                cursor: item.isSample ? 'pointer' : 'grab',
                display: 'flex',
                fontWeight: 500,
                alignItems: 'center',
                justifyContent: 'space-between',
                border: 'none',
                color: '#374151',
                backgroundColor:
                  isActive || snapshot.isDragging ? '#F0F2F5' : 'inherit',
                borderRight: isActive
                  ? '3px solid #4285F4'
                  : '3px solid transparent',
                transition: 'background-color 0.2s',
              }}
            >
              {isFolderContent && (
                <>
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 22,
                      width: 1,
                      height: isLast ? '50%' : '100%',
                      backgroundColor: '#D1D5DB',
                      pointerEvents: 'none',
                    }}
                  />
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: 22,
                      width: 12,
                      height: 1,
                      backgroundColor: '#D1D5DB',
                      pointerEvents: 'none',
                    }}
                  />
                </>
              )}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {keyword}
                </span>
                {item.isSample && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#1971C2',
                      backgroundColor: '#E7F5FF',
                      border: '1px solid #A5D8FF',
                      borderRadius: 4,
                      padding: '1px 5px',
                      flexShrink: 0,
                      lineHeight: '16px',
                    }}
                  >
                    샘플 데이터
                  </span>
                )}
              </span>
              {!item.isSample && (
                <EllipsisDropdown
                  menu={
                    <DynamicDropdownMenu
                      keywordId={keywordId}
                      keyword={keyword}
                      index={globalIndex}
                      onRemove={removeKeywordList}
                      onKeywordDefinition={handleKeywordDefinition}
                      onCompetitorKeywordDefinition={
                        handleCompetitorKeywordDefinition
                      }
                      currentFolderId={item.folderId ?? null}
                      folderOptions={folderOptions}
                      onMoveFolder={moveKeywordByMenu}
                    />
                  }
                />
              )}
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const folderMenu = (folder: FolderNode) => (
    <Menu
      items={[
        {
          key: 'rename',
          label: '이름 변경',
          icon: <EditOutlined />,
          onClick: (event) => {
            event.domEvent.stopPropagation();
            openFolderModal({ mode: 'rename', folder });
          },
        },
        {
          key: 'delete',
          label: '삭제',
          icon: <DeleteOutlined />,
          onClick: (event) => {
            event.domEvent.stopPropagation();
            removeFolder(folder);
          },
        },
      ]}
    />
  );

  const renderFolderLabel = (folder: FolderNode) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
      }}
    >
      {folder.isCollapsed ? (
        <FolderOutlined style={{ color: '#8C98A4', flexShrink: 0 }} />
      ) : (
        <FolderOpenOutlined style={{ color: '#2F80ED', flexShrink: 0 }} />
      )}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {folder.name}
      </span>
    </span>
  );

  const renderStaticKeywordPreview = (
    item: AiRealtimeKeywordItem,
    index: number,
    isLast: boolean,
  ) => (
    <div
      key={item.keywordId ?? `${item.keyword}-${index}`}
      style={{
        position: 'relative',
        minHeight: '40px',
        width: '100%',
        fontSize: '14px',
        padding: `4px 8px 4px ${FOLDER_CHILD_TEXT_LEFT}px`,
        display: 'flex',
        fontWeight: 500,
        alignItems: 'center',
        justifyContent: 'space-between',
        border: 'none',
        color: '#374151',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          width: 1,
          height: isLast ? '50%' : '100%',
          backgroundColor: '#D1D5DB',
          pointerEvents: 'none',
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          left: 22,
          width: 12,
          height: 1,
          backgroundColor: '#D1D5DB',
          pointerEvents: 'none',
        }}
      />
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          minWidth: 0,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.keyword}
        </span>
        {item.isSample && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#1971C2',
              backgroundColor: '#E7F5FF',
              border: '1px solid #A5D8FF',
              borderRadius: 4,
              padding: '1px 5px',
              flexShrink: 0,
              lineHeight: '16px',
            }}
          >
            샘플 데이터
          </span>
        )}
      </span>
    </div>
  );

  const renderStaticKeywordList = (items: AiRealtimeKeywordItem[]) => (
    <div
      style={{
        minHeight: items.length === 0 ? 32 : undefined,
      }}
    >
      {items.map((item, index) =>
        renderStaticKeywordPreview(item, index, index === items.length - 1),
      )}
      {items.length === 0 && (
        <div
          style={{
            minHeight: 32,
            display: 'flex',
            alignItems: 'center',
            padding: `4px 8px 4px ${KEYWORD_TEXT_LEFT}px`,
            color: '#9CA3AF',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          등록된 키워드가 없습니다.
        </div>
      )}
    </div>
  );

  const renderFolderDragClone = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric,
  ) => {
    const folder = orderedFolders[rubric.source.index];
    if (!folder) {
      return (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={provided.draggableProps.style}
        />
      );
    }

    const items = folder.items ?? [];
    const showKeywordPreview = !folder.isCollapsed;

    const baseStyle = provided.draggableProps.style as
      | React.CSSProperties
      | undefined;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={{
          ...baseStyle,
          height: showKeywordPreview ? 'auto' : baseStyle?.height,
          overflow: 'visible',
          borderRadius: 4,
          backgroundColor: '#FFFFFF',
          boxShadow: snapshot.isDragging
            ? '0 8px 20px rgba(15, 23, 42, 0.14)'
            : undefined,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'relative',
            minHeight: '40px',
            width: '100%',
            fontSize: '14px',
            padding: `4px 8px 4px ${FOLDER_ROW_LEFT}px`,
            display: 'flex',
            fontWeight: 700,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: 'none',
            borderBottom: 'none',
            borderLeft: 'none',
            borderRight: '3px solid transparent',
            color: '#1F2937',
            letterSpacing: '-0.01em',
            backgroundColor: '#F0F2F5',
          }}
        >
          {showKeywordPreview && items.length > 0 && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: '70%',
                bottom: 0,
                left: 22,
                width: 1,
                backgroundColor: '#D1D5DB',
                pointerEvents: 'none',
              }}
            />
          )}
          {renderFolderLabel(folder)}
        </div>
        {showKeywordPreview && renderStaticKeywordList(items)}
      </div>
    );
  };

  const getFolderDraggableStyle = (
    style: DraggableProvided['draggableProps']['style'],
  ): React.CSSProperties => ({
    ...style,
    position: style && 'position' in style ? style.position : 'relative',
  });

  const renderKeywordDroppable = (
    droppableId: string,
    items: AiRealtimeKeywordItem[],
    options?: {
      showEmptyText?: boolean;
      isFolderContent?: boolean;
    },
  ) => (
    <Droppable droppableId={droppableId} type="KEYWORD">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          style={{
            minHeight: items.length === 0 ? 32 : undefined,
            backgroundColor: snapshot.isDraggingOver ? '#F8FAFF' : 'inherit',
            transition: 'background-color 0.2s',
          }}
        >
          {items.map((item, index) =>
            renderKeywordItem(item, index, allKeywords.indexOf(item), {
              isFolderContent: options?.isFolderContent,
              isLast: index === items.length - 1,
            }),
          )}
          {items.length === 0 && options?.showEmptyText && (
            <div
              style={{
                minHeight: 32,
                display: 'flex',
                alignItems: 'center',
                padding: `4px 8px 4px ${
                  options?.isFolderContent
                    ? FOLDER_CHILD_TEXT_LEFT
                    : KEYWORD_TEXT_LEFT
                }px`,
                color: '#9CA3AF',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              등록된 키워드가 없습니다.
            </div>
          )}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        marginBottom: '16px',
      }}
    >
      <div style={{ padding: '12px 12px 8px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1px 1fr',
            overflow: 'hidden',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #4285F4, #7B61FF)',
          }}
        >
          <button
            onClick={() => setShowAddKeywordModal(true)}
            style={{
              width: '100%',
              height: '42px',
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <PlusOutlined />
            키워드 등록
          </button>
          <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.28)' }} />
          <Tooltip
            title={
              folderLimitReached
                ? '폴더는 최대 10개까지 생성할 수 있습니다'
                : undefined
            }
          >
            <span>
              <button
                disabled={folderLimitReached}
                onClick={() => openFolderModal({ mode: 'create' })}
                style={{
                  width: '100%',
                  height: '42px',
                  backgroundColor: folderLimitReached
                    ? '#E9ECEF'
                    : 'transparent',
                  border: 'none',
                  color: folderLimitReached ? '#868E96' : '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: folderLimitReached ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <FolderAddOutlined />
                폴더 생성
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {allKeywords.length === 0 && (
        <div
          role="presentation"
          onClick={navigateToEmptyFeature}
          style={{
            minHeight: '40px',
            width: '100%',
            fontSize: '14px',
            padding: `4px 8px 4px ${KEYWORD_TEXT_LEFT}px`,
            cursor: 'pointer',
            display: 'flex',
            fontWeight: 500,
            alignItems: 'center',
            justifyContent: 'space-between',
            border: 'none',
            color: '#374151',
            backgroundColor: location.pathname.endsWith('/new')
              ? '#F0F2F5'
              : 'inherit',
            borderRight: location.pathname.endsWith('/new')
              ? '3px solid #4285F4'
              : '3px solid transparent',
          }}
        >
          새 키워드
        </div>
      )}

      <DragDropContext
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        <Droppable
          droppableId={FOLDER_ORDER_DROPPABLE_ID}
          type="FOLDER"
          renderClone={renderFolderDragClone}
        >
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {orderedFolders.map((folder, folderIndex) => {
                const items = folder.items ?? [];
                const folderId = folder.folderId;

                return (
                  <Draggable
                    draggableId={folderId}
                    index={folderIndex}
                    key={folderId}
                  >
                    {(folderProvided, snapshot) => {
                      const draggableStyle =
                        folderProvided.draggableProps.style;

                      return (
                        <div
                          ref={folderProvided.innerRef}
                          {...folderProvided.draggableProps}
                          style={getFolderDraggableStyle(draggableStyle)}
                        >
                          <Droppable
                            droppableId={getKeywordAreaId(folderId)}
                            type="KEYWORD"
                          >
                            {(droppableProvided, droppableSnapshot) => (
                              <div
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                              >
                                <div
                                  {...folderProvided.dragHandleProps}
                                  role="presentation"
                                  onClick={() => {
                                    if (suppressFolderClickRef.current) {
                                      return;
                                    }
                                    toggleFolderCollapsed(folder);
                                  }}
                                  style={{
                                    position: 'relative',
                                    minHeight: '40px',
                                    width: '100%',
                                    fontSize: '14px',
                                    padding: `4px 8px 4px ${FOLDER_ROW_LEFT}px`,
                                    cursor: snapshot.isDragging
                                      ? 'grabbing'
                                      : 'grab',
                                    display: 'flex',
                                    fontWeight: 700,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderTop: 'none',
                                    borderBottom: 'none',
                                    borderLeft: 'none',
                                    borderRight: '3px solid transparent',
                                    color: '#1F2937',
                                    letterSpacing: '-0.01em',
                                    backgroundColor:
                                      snapshot.isDragging ||
                                      droppableSnapshot.isDraggingOver
                                        ? '#F0F2F5'
                                        : 'inherit',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(event) => {
                                    event.currentTarget.style.backgroundColor =
                                      '#F0F2F5';
                                  }}
                                  onMouseLeave={(event) => {
                                    event.currentTarget.style.backgroundColor =
                                      snapshot.isDragging ||
                                      droppableSnapshot.isDraggingOver
                                        ? '#F0F2F5'
                                        : 'inherit';
                                  }}
                                >
                                  {!folder.isCollapsed && items.length > 0 && (
                                    <span
                                      aria-hidden
                                      style={{
                                        position: 'absolute',
                                        top: '70%',
                                        bottom: 0,
                                        left: 22,
                                        width: 1,
                                        backgroundColor: '#D1D5DB',
                                        pointerEvents: 'none',
                                      }}
                                    />
                                  )}
                                  {renderFolderLabel(folder)}
                                  <span
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: '#374151',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <EllipsisDropdown
                                      menu={folderMenu(folder)}
                                    />
                                  </span>
                                </div>
                                {!folder.isCollapsed &&
                                  items.map((item, idx) =>
                                    renderKeywordItem(
                                      item,
                                      idx,
                                      allKeywords.indexOf(item),
                                      {
                                        isFolderContent: true,
                                        isLast: idx === items.length - 1,
                                      },
                                    ),
                                  )}
                                {!folder.isCollapsed &&
                                  items.length === 0 && (
                                    <div
                                      style={{
                                        minHeight: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: `4px 8px 4px ${FOLDER_CHILD_TEXT_LEFT}px`,
                                        color: '#9CA3AF',
                                        fontSize: 12,
                                        fontWeight: 500,
                                      }}
                                    >
                                      등록된 키워드가 없습니다.
                                    </div>
                                  )}
                                {droppableProvided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      );
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        {orderedFolders.length > 0 && unassigned.length > 0 && (
          <div
            style={{
              margin: `12px 12px 4px ${FOLDER_ROW_LEFT}px`,
              paddingTop: 10,
              borderTop: '1px solid #E9ECEF',
              color: '#9CA3AF',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              lineHeight: '18px',
            }}
          >
            폴더 없음
          </div>
        )}
        {renderKeywordDroppable(UNASSIGNED_DROPPABLE_ID, unassigned)}
      </DragDropContext>

      {showAddKeywordModal && (
        <RealtimeKeywordModal
          onClose={() => setShowAddKeywordModal(false)}
          onSuccess={async (keywordId) => {
            setShowAddKeywordModal(false);
            await invalidateKeywordQueries();
            navigate(
              `${getCurrentFeaturePath(location.pathname)}?id=${keywordId}`,
            );
          }}
          showFolderSelect
          folderOptions={folderOptions}
        />
      )}

      {folderModal && (
        <Modal
          open
          title={folderModal.mode === 'create' ? '폴더 생성' : '폴더 이름 변경'}
          okText={folderModal.mode === 'create' ? '생성' : '변경'}
          cancelText="취소"
          confirmLoading={
            createFolderMutation.isLoading || renameFolderMutation.isLoading
          }
          okButtonProps={{ disabled: isFolderNameDuplicated }}
          onCancel={closeFolderModal}
          onOk={submitFolderModal}
        >
          <Input
            maxLength={20}
            placeholder="폴더 이름을 입력해 주세요."
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
            onPressEnter={submitFolderModal}
            status={isFolderNameDuplicated ? 'error' : undefined}
          />
          {isFolderNameDuplicated && (
            <div
              style={{
                color: '#ff4d4f',
                fontSize: 12,
                marginTop: 6,
              }}
            >
              이미 사용 중인 이름입니다.
            </div>
          )}
        </Modal>
      )}

      {showDefinitionModal && selectedKeyword && (
        <KeywordDefinitionModal
          visible={showDefinitionModal}
          keyword={selectedKeyword.keyword}
          keywordId={selectedKeyword.keywordId}
          onClose={() => {
            setShowDefinitionModal(false);
            setSelectedKeyword(null);
          }}
          onModify={() => {
            console.log('수정 버튼 클릭');
          }}
        />
      )}
    </div>
  );
};

export default KeywordFolderTree;
